// Phase 12 — SE Forms List (applicable forms for current field user)
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileText, ChevronRight, Filter } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const CATEGORY_COLOR = {
  general: "bg-neutral-100 text-neutral-800 border-neutral-200",
  btl: "bg-purple-100 text-purple-800 border-purple-200",
  display: "bg-blue-100 text-blue-800 border-blue-200",
  market_survey: "bg-amber-100 text-amber-900 border-amber-200",
  distributor_admin: "bg-rose-100 text-rose-800 border-rose-200",
  other: "bg-neutral-100 text-neutral-800 border-neutral-200",
};

export default function FormsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [params] = useSearchParams();
  const visit_id = params.get("visit_id");
  const retailer_id = params.get("retailer_id");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/activity-forms/applicable");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = items.filter(f => {
    if (category && f.category !== category) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!(f.name?.toLowerCase().includes(s) || f.code?.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const categories = Array.from(new Set(items.map(f => f.category).filter(Boolean)));

  const linkQ = visit_id || retailer_id
    ? `?${visit_id ? `visit_id=${visit_id}&` : ""}${retailer_id ? `retailer_id=${retailer_id}` : ""}`
    : "";

  return (
    <div className="max-w-2xl mx-auto p-4" data-testid="forms-list-page">
      <div className="mb-4">
        <h1 className="text-xl font-semibold">Activity Forms</h1>
        <p className="text-sm text-neutral-500">
          {visit_id ? "This submission will be linked to your current visit." : "Choose a form to fill."}
        </p>
      </div>

      <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
             className="mb-3" data-testid="forms-search" />
      {categories.length > 1 && (
        <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
          <button data-testid="cat-chip-all"
                  onClick={() => setCategory("")}
                  className={`text-xs px-3 py-1 rounded-full border whitespace-nowrap ${!category ? "bg-emerald-600 text-white border-emerald-700" : "bg-white border-neutral-300"}`}>
            All
          </button>
          {categories.map(c => (
            <button key={c} data-testid={`cat-chip-${c}`}
                    onClick={() => setCategory(c)}
                    className={`text-xs px-3 py-1 rounded-full border whitespace-nowrap capitalize ${category === c ? "bg-emerald-600 text-white border-emerald-700" : "bg-white border-neutral-300"}`}>
              {c.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center text-neutral-400 py-10">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed rounded-lg text-neutral-400">
          <FileText className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
          No forms available right now.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(f => (
            <Link key={f.id} to={`/forms/${f.id}/fill${linkQ}`}
                  className="block bg-white border rounded-lg p-4 shadow-sm hover:border-emerald-500 transition"
                  data-testid={`form-card-${f.code}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${CATEGORY_COLOR[f.category] || CATEGORY_COLOR.other}`}>
                      {(f.category || "general").replace(/_/g, " ")}
                    </span>
                    <span className="text-[10px] text-neutral-400 font-mono">v{f.version}</span>
                  </div>
                  <h3 className="font-medium leading-snug">{f.name}</h3>
                  {f.description && <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{f.description}</p>}
                  <div className="text-[11px] text-neutral-400 mt-2">{f.field_count} field{f.field_count === 1 ? "" : "s"}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-neutral-400 mt-1" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
